import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import '../App.css';
import { Modal, ScrollArea, Title, CloseButton, Group, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import ImageSlideDeck from '../Components/ImageSlideDeck';
const BookNow = lazy(() => import('./BookNow'));
import CruiseImg from '../Components/CruiseImg';
import HotelImg from '../Components/HotelImg';
import TransportationOptions from '../Components/TransportationOptions';
import TourGrid from '../Components/TourGrid';
import MoreToursBtn from '../Components/MoreToursBtn';
import BundleTours from '../Components/BundleTours';
import type { Bundle, Tour } from '../types';

const transportationOptions = [
    {
        url: "/tours/AllTours",
        title: "Placencia Area Hotel Excursions",
        imgComponent: HotelImg,
        description: [
            "Staying in Placencia? Let us know where, and we can come and get you for a day filled with as many adventures as you could possibly want.",
            "There will be as much time as you need to relax, and stop at whatever sites and attractions take your fancy along the journey!",
            "We will take you to anything that crosses your mind, and drive you back to where you are staying when your journey is over."
        ]
    },
    {
        url: "/tours/AllTours",
        title: "Cruise Excursions",
        imgComponent: CruiseImg,
        description: [
            "Guests arriving from Norweigan Cruise Lines will be docking at the private island harvest Caye just a 20 minute boat ride offshore from the Placencia Peninsula.",
            "Catch one of the three morning ferries to Placencia Docks, where your guides will be waiting for a sign to take you to your adventure!",
            "You will have plenty of time to see the sites before we get you back to the Placencia Docks to make the ferry back to Harvest Caye"
        ]
    },
];

const introText = [
    "Placencia Action Tours is your number one choice in tour operations when visiting the Placencia peninsula.",
    "We offer tours in the southern region of Belize and with sister companies all across the country we are the most viable choice for reaching any and all of your vacation goals while visiting Beautiful Belize."
];

const bundleText = [
    "Extend your tour adventure and get fantastic savings by adding on some additional tours! Your professional guides will take you to multiple locations in the day so that you can make the most out of your time here.",
    "Be sure to filter on where you are residing so that we can make sure you have as much time as possible to see the wonders of Belize."
];

interface HomeProps {
    tours: Tour[];
    bundles: Bundle[];
    homeSlideImages: Record<string, string>;
    arrivalMode: 'hotel' | 'cruise';
    onArrivalChange: (mode: 'hotel' | 'cruise') => void;
}

function Home({ tours, bundles, homeSlideImages, arrivalMode, onArrivalChange }: HomeProps) {
    // Prepare slide images
    const preparedSlideImages = useMemo(() => {
        const images = { ...homeSlideImages };
        images["name"] = "home";
        return images;
    }, [homeSlideImages]);

    // Sort tours
    const allTours = useMemo(() => {
        return [...tours].sort((a, b) => {
            if (a.name === "waterfalls") return -1;
            if (b.name === "waterfalls") return 1;
            if (a.name === b.name) return 0;
            return a.name < b.name ? -1 : 1;
        });
    }, [tours]);
    const filteredTours = useMemo(() => {
        return arrivalMode === 'cruise'
            ? allTours.filter((tour) => tour.cruise)
            : allTours;
    }, [allTours, arrivalMode]);

    const getVisibleCount = (width: number) => {
        if (width >= 1200) return 3;
        if (width >= 900) return 2;
        return 1;
    };

    const [visibleCount, setVisibleCount] = useState<number>(() => {
        if (typeof window === 'undefined') return 3;
        return Math.max(3, getVisibleCount(window.innerWidth));
    });
    const [showAll, setShowAll] = useState<boolean>(false);
    const [displayedTours, setDisplayedTours] = useState<Tour[]>(filteredTours.slice(0, visibleCount));
    const [bookingBundle, setBookingBundle] = useState<string[] | null>(null);
    const [bookingOpened, { open: openBooking, close: closeBooking }] = useDisclosure(false);
    const [showDeferred, setShowDeferred] = useState(false);

    const toggleBooking = (bundle?: string[] | null) => {
        setBookingBundle(bundle ?? null);
        openBooking();
    };

    useEffect(() => {
        const handleResize = () => {
            const nextCount = Math.max(3, getVisibleCount(window.innerWidth));
            setVisibleCount(nextCount);
            if (!showAll) {
                setDisplayedTours(filteredTours.slice(0, nextCount));
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [filteredTours, showAll]);

    useEffect(() => {
        if (showAll) {
            setDisplayedTours(filteredTours);
        } else {
            setDisplayedTours(filteredTours.slice(0, visibleCount));
        }
    }, [filteredTours, visibleCount, showAll]);

    useEffect(() => {
        setShowAll(false);
    }, [arrivalMode]);

    useEffect(() => {
        const reveal = () => setShowDeferred(true);
        if ('requestIdleCallback' in window) {
            const id = window.requestIdleCallback(reveal, { timeout: 1500 });
            return () => window.cancelIdleCallback?.(id);
        }
        const timer = setTimeout(reveal, 800);
        return () => clearTimeout(timer);
    }, []);

    const addTours = () => {
        setShowAll(prev => !prev);
    };

    return (
        <div className="homeContainer">
            {/* Booking Modal */}
            <Modal
                opened={bookingOpened}
                onClose={closeBooking}
                size="xl"
                fullScreen
                radius={0}
                transitionProps={{ transition: 'slide-up', duration: 200 }}
                styles={{
                    header: { display: 'none' },
                    body: { padding: 0 },
                }}
            >
                <Box
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        backgroundColor: 'var(--mantine-color-body)',
                        borderBottom: '1px solid var(--mantine-color-gray-3)',
                        padding: '12px 16px',
                    }}
                >
                    <Group justify="space-between" align="center">
                        <Title order={3}>Reserve Your Adventure</Title>
                        <CloseButton size="lg" onClick={closeBooking} />
                    </Group>
                </Box>
                <ScrollArea style={{ height: 'calc(100vh - 60px)' }}>
                    <Box p="md">
                        <Suspense fallback={<div>Loading booking form...</div>}>
                            <BookNow
                                selectedTours={bookingBundle}
                                bundles={bundles}
                                tours={tours}
                                arrivalMode={arrivalMode}
                            />
                        </Suspense>
                    </Box>
                </ScrollArea>
            </Modal>

            <ImageSlideDeck images={preparedSlideImages} alts={[]} />
            <div className="homeBodyContainer">
                <h1 className="homeTitle">Southern Belize Group Tours</h1>
                <div className="introText">
                    {introText.map((textBlock, index) => (
                        <p key={index} className="homeTextBlock">{textBlock}</p>
                    ))}
                </div>
                <div>
                    <h2 className="homeTitle centered">How are you getting to Placencia?</h2>
                    <div className="introText" style={{ textAlign: 'center' }}>
                        {arrivalMode === 'hotel'
                            ? "Most tours are available for hotel guests. We'll tailor options for your schedule."
                            : "Cruise guests have time-sensitive options. We'll surface tours that fit your port window."}
                    </div>
                    {showDeferred ? (
                        <>
                            <TransportationOptions
                                transportationOptions={transportationOptions}
                                selectedMode={arrivalMode}
                                onSelect={onArrivalChange}
                            />
                            <h2 className="tourTitle">
                                {arrivalMode === 'cruise' ? "Popular Tours for Cruise Guests" : "Popular Tours"}
                            </h2>
                            <TourGrid
                                tours={displayedTours}
                                bookTourFunction={toggleBooking}
                                filter={arrivalMode === 'cruise' ? 'cruise' : undefined}
                                reducedHeight={displayedTours.length <= 3}
                            />
                            <MoreToursBtn clickFunction={addTours} expanded={showAll} />
                            {bundles.length > 0 && (
                                <BundleTours
                                    clickBundleFunction={toggleBooking}
                                    tours={allTours}
                                    bundles={bundles}
                                    bundleText={bundleText}
                                    bundleTitle="Bundle Tours and Save!"
                                />
                            )}
                        </>
                    ) : (
                        <div className="introText" style={{ textAlign: 'center' }}>
                            Loading tours…
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Home;
